import { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";

import Page from "../Components/Page";

import DisabledIndicatorPanel from "../Components/Indicators/DisabledIndicatorPanel";
import IndicatorGHG from "../Components/Indicators/IndicatorGHG";
import IndicatorSummaryRow from "../Components/Indicators/IndicatorSummaryRow";
import energyUseIcon from "../Assets/Icons/energyUseIcon.png";
import landUseIcon from "../Assets/Icons/landUseIcon.png";
import soilErosionRiskIcon from "../Assets/Icons/soilErosionRiskIcon.png";

import {
  fetchAnalysis,
  fetchCropYear,
  fetchFarm,
  fetchField,
} from "../Components/axiosFetchers";
import { useAuth } from "../Components/Auth/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import {
  ResourceBreadcrumb,
  buildCropYearBreadcrumb,
  formatCropYearLabel,
} from "../Components/ResourceBreadcrumb.jsx";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  DATA_READ_SCOPE,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";
import AnalysisHistory from "../Components/AnalysisHistory.jsx";
import { buildFieldContextQuickFacts } from "../utils/resourceQuickFacts.js";
import { isHolosCropSupported } from "../constants/holos.js";

const DISABLED_INDICATOR_COPY =
  "Support coming soon.";

export default function AnalysisPage({ requireSelectedGrower = false }) {
  const { user } = useAuth();
  const { farmId, fieldId, cropyearId } = useParams();
  const navigate = useNavigate();
  const selectedAggregatorGrower = useSelectedAggregatorGrower();
  const selectedAggregatorGrowerPublicId =
    selectedAggregatorGrower?.grower_profile_public_id || "";
  const canReadSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_READ_SCOPE);

  document.title = "Analysis Results Page - Field To Market Canada";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [farm, setFarm] = useState(null);
  const [field, setField] = useState(null);
  const [cropYear, setCropYear] = useState(null);

  async function analysisPageStartUp() {
    if (
      requireSelectedGrower &&
      (!selectedAggregatorGrowerPublicId || !canReadSelectedGrower)
    ) {
      setLoading(false);
      setReportData(null);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const [nextFarm, nextField, nextCropYear, nextReportData] =
        await Promise.all([
          fetchFarm(farmId, user),
          fetchField(farmId, fieldId, user),
          fetchCropYear(farmId, fieldId, cropyearId, user),
          fetchAnalysis(cropyearId, user),
        ]);
      setFarm(nextFarm);
      setField(nextField);
      setCropYear(nextCropYear);
      if (nextReportData?.indicators?.ghg_source !== "holos") {
        setReportData(null);
        setLoadError(
          "No Holos-backed analysis has been generated for this crop year yet."
        );
        return;
      }
      setReportData(nextReportData);
    } catch (error) {
      setReportData(null);
      setLoadError(
        error?.response?.status === 404
          ? "No analysis has been generated for this crop year yet."
          : "We ran into a problem loading this analysis. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    analysisPageStartUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canReadSelectedGrower,
    farmId,
    fieldId,
    cropyearId,
    requireSelectedGrower,
    selectedAggregatorGrowerPublicId,
    user,
  ]);

  const LiveAnalysis = () => {
    const { labels, values, indicators } = reportData;
    const crop = indicators?.crop || cropYear?.crop_this_year || "";
    const year = indicators?.crop_year || cropYear?.crop_year || "";
    const ghgSource = indicators?.ghg_source;

    return (
      <div className="w-full h-full">
        <IndicatorSummaryRow
          labels={labels}
          values={values}
          yieldUnit={cropYear?.yield_units}
        />

        <div className="w-full py-8 text-[#666666]">
          <h2 className="text-[24px] text-[rgba(0,0,0,0.87)] mb-4">
            Fieldprint Data per Indicator
          </h2>

          <IndicatorGHG
            crop={crop}
            year={year}
            value={values[2]}
            ghgSource={ghgSource}
            cropyearId={cropyearId}
            cropYearVersionId={cropYear?.current_version_id}
            isSupportedCrop={isHolosCropSupported(
              cropYear?.crop_this_year, cropYear?.holos_supported_crops
            )}
          />

          <DisabledIndicatorPanel
            icon={landUseIcon}
            title="Land Use"
            message={DISABLED_INDICATOR_COPY}
          />

          <DisabledIndicatorPanel
            icon={energyUseIcon}
            title="Energy Use"
            message={DISABLED_INDICATOR_COPY}
          />

          <DisabledIndicatorPanel
            icon={soilErosionRiskIcon}
            title="Soil Erosion Risk"
            message={DISABLED_INDICATOR_COPY}
          />

          <AnalysisHistory
            cropyearId={cropyearId}
            currentVersionId={cropYear?.current_version_id}
            user={user}
          />
        </div>
      </div>
    );
  };

  return (
    <Page
      showQuickFacts={true}
      title={formatCropYearLabel(cropYear)}
      headerBorderColor={"border-[#34a853]"}
      headerVariant="hybrid"
      contentWidth="wide"
      headerActions={reportData ? <Button variant="outlined" onClick={() => {
        const blob = new Blob([JSON.stringify({
          exported_at: new Date().toISOString(),
          analysis_is_current: Boolean(cropYear?.analysis_is_current),
          saved_analysis: reportData,
        }, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `fieldprint-analysis-${cropyearId}.json`;
        link.click();
        URL.revokeObjectURL(url);
      }}>Download saved analysis</Button> : null}
      quickFactsVariant="inline-card"
      introContent={
        farm?.name || field?.name ? (
          <p>{[farm?.name, field?.name].filter(Boolean).join(" / ")}</p>
        ) : null
      }
      quickFactsData={[
        ...buildFieldContextQuickFacts({ farm, field, auditRecord: cropYear }),
        { label: "Crop", value: cropYear?.crop_this_year || null },
        { label: "Crop Year", value: cropYear?.crop_year || null },
      ]}
    >
      <div className="h-full w-full ">
        {requireSelectedGrower ? (
          <div className="mb-5">
            <AggregatorSelectedGrowerPanel
              selectedGrower={selectedAggregatorGrower}
            />
          </div>
        ) : null}
        {requireSelectedGrower &&
        selectedAggregatorGrowerPublicId &&
        !canReadSelectedGrower ? (
          <AggregatorScopeWarning
            message="This selected grower link cannot view analysis records."
            requiredScope={DATA_READ_SCOPE}
          />
        ) : null}
        {requireSelectedGrower &&
        (!selectedAggregatorGrowerPublicId || !canReadSelectedGrower) ? null : (
          <>
        <ResourceBreadcrumb
          ariaLabel="Crop year location"
          items={buildCropYearBreadcrumb({
            cropYear,
            cropyearId,
            farm,
            farmId,
            field,
            fieldId,
          })}
        />
        {loading && (
          <div className="w-full flex justify-center py-16">
            <CircularProgress />
          </div>
        )}

        {!loading && loadError && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={analysisPageStartUp}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {!loading &&
        !loadError &&
        reportData &&
        cropYear?.analysis_id &&
        !cropYear?.analysis_is_current ? (
          <Alert
            severity="warning"
            className="mb-5"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() =>
                  navigate(
                    `/farm/${farmId}/field/${fieldId}/cropyear/${cropyearId}/addanalysis`
                  )
                }
              >
                Review and regenerate
              </Button>
            }
          >
            This analysis uses an earlier crop-year version. It remains
            available for comparison, but it does not represent the current
            saved inputs.
          </Alert>
        ) : null}

        {!loading &&
          !loadError &&
          reportData &&
          typeof reportData === "object" &&
          "indicators" in reportData && <LiveAnalysis />}
          </>
        )}
      </div>
    </Page>
  );
}
